import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { GigyaAPI, useGigyaStore } from '@ciam/gigya-api';
import { useMfaStore } from '@features/login';
import { pageRedirects, useLoginInfoStore } from '@features/shared';
import { useModal } from '@ciam/ui';
import { useTranslation } from '@ciam/i18n';

export const useAdLogin = () => {
  const { openModal } = useModal();
  const { t } = useTranslation(['login', 'schemas']);
  const [searchParams] = useSearchParams();
  const gig_events = searchParams.get('gig_events') ?? '';

  const { hasMfaSession, executeMfaLogin } = useMfaStore();
  const isGigyaScriptLoaded = useGigyaStore((state) => state.isGigyaScriptLoaded);
  const { setUserInfo } = useLoginInfoStore();

  const checkAdLoginSuccess = useCallback(async () => {
    const errorCode = searchParams.get('errorCode') ?? '';
    const errorMessage = searchParams.get('errorMessage	') ?? '';
    const uid = searchParams.get('UID') ?? '';
    const signatureTimestamp = searchParams.get('signatureTimestamp') ?? '';
    const uidSignature = searchParams.get('UIDSignature') ?? '';
    const email = searchParams.get('email') ?? '';

    // AD 로그인 후 검증
    switch (errorCode) {
      case '0': {
        await setUserInfo({
          email: email,
          uid: uid,
          uidSignature: uidSignature,
          signatureTimestamp: signatureTimestamp,
          isAdLogin: true,
        });

        if (!hasMfaSession({ email })) {
          const jwtResponse = await executeMfaLogin({
            email: email,
            uid: uid,
            uidSignature: uidSignature,
            signatureTimestamp: signatureTimestamp,
          });
          return window.location.assign(jwtResponse);
        }

        return await GigyaAPI.socialize.notifyLogin({
          dontHandleScreenSet: true,
          siteUID: uid,
          UIDSig: uidSignature,
          UIDTimestamp: signatureTimestamp,
        });
      }
      case '206001':
        return pageRedirects.approvalStatusError('pending');
      default:
        return openModal({
          type: 'error',
          title: t('errors.ad-error-title'),
          description: [t('errors.ad-error-description'), `${errorMessage}`],
        });
    }
  }, [searchParams, openModal, t, setUserInfo, hasMfaSession, executeMfaLogin]);

  // AD 로그인 체크
  useEffect(() => {
    if (!isGigyaScriptLoaded) return;

    const isAdLogin = gig_events;
    if (isAdLogin) checkAdLoginSuccess();
  }, [isGigyaScriptLoaded, gig_events, checkAdLoginSuccess]);
};
